import React from 'react';
import { Spin, Card, Typography, Table, Modal, Tag, Row, Col } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useContract } from '../../../../../../../../Hooks/TT_ELD/Contracts';

const { Title, Text } = Typography;

interface CompanyContract {
  id: string;
  amount: number;
  companyId: number;
  contractId: string;
  company: {
    id: number;
    name: string;
    usdot: string;
    uid: string;
    email: string;
  };
}

interface ContractPreviewModalProps {
  id: string | null;
  open: boolean;
  onClose: () => void;
}

const PreviewModal: React.FC<ContractPreviewModalProps> = ({ id, open, onClose }) => {
  const { data, isLoading } = useContract(id || '');

  if (!id) return null;

  const contract = data?.data;

  const columns: ColumnsType<CompanyContract> = [
    { title: 'Company Name', dataIndex: ['company', 'name'], key: 'name' },
    { title: 'USDOT', dataIndex: ['company', 'usdot'], key: 'usdot' },
    { title: 'Email', dataIndex: ['company', 'email'], key: 'email' },
    { title: 'Truck Amount', dataIndex: 'amount', key: 'amount' },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return '#D7D8E0';
      case 'accepted':
        return '#27AE60';
      case 'rejected':
        return '#F64747';
      case 'expired':
        return '#F99E2C';
      default:
        return 'blue';
    }
  };

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={900} title="Contract Preview">
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Spin size="default" />
        </div>
      ) : (
        <>
          <Card style={{ marginBottom: 24 }}>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <div>
                  <Text type="secondary">Contract Id</Text>
                  <div>#{contract.contractId}</div>
                </div>
              </Col>
              <Col span={6}>
                <div>
                  <Text type="secondary">Contract Type</Text>
                  <div>
                    {contract.isDashcam ? 'Contract with dashcam' : 'Contract with platform'}
                  </div>
                </div>
              </Col>
              <Col span={4}>
                <div>
                  <Text type="secondary">Amount</Text>
                  <div>{contract.amount}</div>
                </div>
              </Col>
              <Col span={4}>
                {contract.isDashcam && (
                  <div>
                    <Text type="secondary">Dashcam Amount</Text>
                    <div>{contract.amountDashcam}</div>
                  </div>
                )}
              </Col>
              <Col span={4}>
                <div>
                  <Text type="secondary">Stripe Quantity</Text>
                  <div>{contract.stripeQuantity}</div>
                </div>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col span={4}>
                <div>
                  <Text type="secondary">Platform</Text>
                  <div>{contract.platform}</div>
                </div>
              </Col>
              <Col span={4}>
                <div>
                  <Text type="secondary">Status</Text>
                  <div>
                    <Tag
                      color={getStatusColor(contract.status)}
                      style={{
                        textTransform: 'capitalize',
                      }}
                    >
                      {contract.status}
                    </Tag>
                  </div>
                </div>
              </Col>
              <Col span={4}>
                <div>
                  <Text type="secondary">Period</Text>
                  <div>{contract.period} months</div>
                </div>
              </Col>
              <Col span={4}>
                {contract.status !== 'pending' && (
                  <>
                    <div>
                      <Text type="secondary">Start date</Text>
                      <div>
                        {contract.startDate
                          ? dayjs(contract.startDate).format('MMM DD, YYYY')
                          : '--'}
                      </div>
                    </div>
                  </>
                )}
              </Col>
              <Col span={4}>
                {contract.status !== 'pending' && (
                  <>
                    <div>
                      <Text type="secondary">End date</Text>
                      <div>
                        {contract.endDate ? dayjs(contract.endDate).format('MMM DD, YYYY') : '--'}
                      </div>
                    </div>
                  </>
                )}
              </Col>
              <Col span={4}>
                <div>
                  <Text type="secondary">Created</Text>
                  <div>
                    {contract.createdAt ? dayjs(contract.createdAt).format('MMM DD, YYYY') : '--'}
                  </div>
                </div>
              </Col>
            </Row>
          </Card>

          <Title level={4} style={{ marginTop: 24 }}>
            Companies
          </Title>
          <Table
            rowKey="id"
            columns={columns}
            dataSource={contract.companyContracts}
            pagination={false}
          />
        </>
      )}
    </Modal>
  );
};

export default PreviewModal;
